import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AppHeader } from "@/components/Layout/AppHeader";
import { ExpenseHistory } from "@/components/ExpenseHistory";
import { ExpenseInputSheet } from "@/components/Expense/ExpenseInputSheet";
import { MonthPickerDrawer } from "@/components/History/MonthPickerDrawer";
import { Button } from "@/components/ui/button";
import { Plus, ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";
import { useUndoStack } from "@/hooks/useUndoStack";
import { useLanguage } from "@/contexts/LanguageContext";

const Expenses = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [showPicker, setShowPicker] = useState(false);
  const { pushUndo } = useUndoStack(() => setRefreshTrigger((p) => p + 1));

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (!session) navigate("/auth");
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (!session) navigate("/auth");
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleRefresh = useCallback(() => {
    setRefreshTrigger((p) => p + 1);
  }, []);

  const now = new Date();
  const isCurrentMonth =
    selectedMonth.getFullYear() === now.getFullYear() &&
    selectedMonth.getMonth() === now.getMonth();

  const goToPrevMonth = () => {
    setSelectedMonth((prev) => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() - 1);
      return d;
    });
  };

  const goToNextMonth = () => {
    if (isCurrentMonth) return;
    setSelectedMonth((prev) => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + 1);
      return d;
    });
  };

  const monthLabel = selectedMonth.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        {t("common.loading")}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader
        showDrawerTrigger={false}
        title="Expenses"
        rightActions={null}
      />

      {/* Back button row */}
      <div className="container mx-auto max-w-2xl px-4 pt-[68px] pb-1">
        <button
          onClick={() => navigate("/budget")}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("nav.budget")}
        </button>
      </div>

      {/* Month navigation bar */}
      <div className="sticky top-[57px] z-10 bg-background/95 backdrop-blur border-b">
        <div className="container mx-auto max-w-2xl flex items-center justify-between px-4 py-3">
          <button
            onClick={goToPrevMonth}
            className="p-2 rounded-full hover:bg-accent transition-colors"
            aria-label="Previous month"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          <button
            onClick={() => setShowPicker(true)}
            className="text-base font-semibold hover:text-primary transition-colors px-3 py-1 rounded-lg hover:bg-accent"
          >
            {monthLabel}
          </button>

          <button
            onClick={goToNextMonth}
            disabled={isCurrentMonth}
            className="p-2 rounded-full hover:bg-accent transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Next month"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      <main className="container mx-auto px-6 pb-24 pt-4 max-w-2xl flex-1">
        <ExpenseHistory
          refreshTrigger={refreshTrigger}
          onExpenseDeleted={handleRefresh}
          pushUndo={pushUndo}
          selectedMonth={selectedMonth}
        />
      </main>

      {/* FAB */}
      <Button
        size="icon"
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg z-50"
        onClick={() => setShowAddExpense(true)}
      >
        <Plus className="h-6 w-6" />
      </Button>

      <ExpenseInputSheet
        open={showAddExpense}
        onOpenChange={setShowAddExpense}
        onExpenseAdded={handleRefresh}
        pushUndo={pushUndo}
      />

      <MonthPickerDrawer
        open={showPicker}
        onOpenChange={setShowPicker}
        selectedMonth={selectedMonth}
        onSelect={setSelectedMonth}
      />
    </div>
  );
};

export default Expenses;
